'use client';

/**
 * Banner informativo sticky mostrato in cima alla pagina quando l'admin sta
 * operando "come un altro utente" (impersonate).
 *
 * AccountSwitcher: incluso nel banner solo quando la sidebar di chrome (admin/vendor)
 * NON è disponibile — cioè sulle pagine pubbliche. Sotto /admin, /partner, /vendor,
 * /excursion il combo vive nell'header della sidebar e qui sarebbe duplicato.
 *
 * Layout: fixed top-0, h-10 (40px). La Navbar si trasla giù dello stesso valore
 * quando il banner e' attivo.
 */
import { ShieldCheck, LogOut, X } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/app/providers';
import AccountSwitcher from './AccountSwitcher';

export default function ImpersonationBanner() {
  const { user, impersonating, showImpersonationBanner, dismissImpersonationBanner, stopImpersonate } = useAuth();
  const router = useRouter();
  const pathname = usePathname() || '';

  const handleExit = async () => {
    await stopImpersonate();
    router.push('/admin');
  };
  // Path-based routing: la sidebar (admin/vendor/excursion chrome) è montata
  // sotto questi prefissi; lì l'AccountSwitcher vive già nell'header sidebar.
  const hasSidebar =
    pathname.startsWith('/admin') ||
    pathname.startsWith('/partner');

  if (!impersonating || !showImpersonationBanner) return null;

  return (
    <div
      role="alert"
      className="fixed top-0 left-0 right-0 z-[110] h-10 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md"
    >
      <div className="max-w-7xl mx-auto h-full px-3 sm:px-4 flex items-center gap-3 text-xs sm:text-sm">
        <ShieldCheck size={14} className="shrink-0" />
        <span className="flex-1 truncate">
          Stai operando come{' '}
          <strong>{user?.name || user?.email || '—'}</strong>
          <span className="hidden sm:inline">
            {' '}· Sessione admin{' '}
            <strong>{impersonating.admin_email}</strong>{' '}preservata
          </span>
        </span>
        {/* AccountSwitcher solo dove non c'è sidebar che lo contiene */}
        {!hasSidebar && (
          <div className="shrink-0">
            <AccountSwitcher />
          </div>
        )}
        {/* Uscita rapida: sempre disponibile nel banner */}
        <button
          type="button"
          onClick={handleExit}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/20 hover:bg-white/30 font-bold text-xs transition-colors"
        >
          <LogOut size={13} /> Esci e torna ad Admin
        </button>
        {/* Chiudi: nasconde il banner per la sessione; si riapre a ogni nuovo login/impersonamento. */}
        <button
          type="button"
          onClick={dismissImpersonationBanner}
          aria-label="Chiudi banner"
          title="Chiudi (si riapre al prossimo accesso/impersonamento)"
          data-cid="impersonation-banner-close"
          className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-lg hover:bg-white/25 transition-colors"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
